/**
 * Movimentacao.js — a Movimentação: cada entrada e saída registrada pelas câmeras, com a evidência (Onda 197)
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * Pedido do Iatagã (23/10/2026): no condomínio, a tela "Saídas" deve dar evidência de TODA a movimentação, entrada e
 * saída, conforme as câmeras que existem — o All Suítes só tem câmera de saída; o La Villette tem as duas e nem tem
 * liberação de saída (quem abre é a câmera). A tela parte de cada passagem registrada (`/api/exit-release/movements`)
 * e, na saída, mostra a decisão da liberação quando houver — com o mesmo "Liberar agora" das Saídas.
 *
 * No condomínio ela é a tela inteira ("Movimentação"). No clube, as Saídas continuam abrindo nas decisões de saída;
 * esta é a visão opcional de entradas, ou de entradas e saídas (decisão do Iatagã, 23/10).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiRepeat, FiRefreshCw, FiMaximize2, FiMinimize2, FiUnlock, FiX, FiFilter, FiLogOut, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import PlacaVeicular from '../components/PlacaVeicular';
import Pagination from '../components/Pagination';
import { hojeLocal } from '../utils/dataLocal';
import '../styles/Saidas.css';

const PAGINA_PADRAO = 50;
const TELA_GRANDE_LINHAS = 12;
const PRAZO_LIBERACAO_MS = 5 * 60 * 1000;   // Onda 185, como nas Saídas

function fileUrl(relPath, token) { return relPath ? `/api/ocr-quality/file/${relPath}?token=${encodeURIComponent(token || '')}` : null; }
function hora(v) { const d = new Date(v); return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('pt-BR'); }
function dataHora(v) { const d = new Date(v); return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR'); }
function dia(v) { const d = new Date(`${v}T12:00:00`); return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR'); }

/** Quem passou, com o dono da placa quando ele existe. */
function Quem({ m }) {
  const o = m.owner;
  if (!o) return <span className="saidas-dono saidas-dono--vazio">{m.kind_label || '—'}</span>;
  const local = [o.bloco, o.unidade].filter(Boolean).join(' / ');
  return (
    <span className="saidas-dono">
      <span className="saidas-dono-nome">{o.nome || '—'}</span>
      <span className="saidas-dono-sub">
        {local && <span>{local}</span>}
        <span className="saidas-badge muted">{o.tipo || m.kind_label}</span>
        {o.ativo === false && <span className="saidas-badge warn">inativo</span>}
        {o.tipo === 'visitante' && o.autorizacao === 'vencida' && <span className="saidas-badge warn">autorização vencida</span>}
        {o.tipo === 'visitante' && o.autorizacao === 'vigente' && <span className="saidas-badge ok">autorizado</span>}
      </span>
    </span>
  );
}

// Pedido do Iatagã (23/10): a seta diz o sentido — para a direita é entrada, para a esquerda é saída.
function Sentido({ tipo }) {
  return tipo === 'entrada'
    ? <span className="saidas-sentido entrada"><FiArrowRight aria-hidden="true" /> Entrada</span>
    : <span className="saidas-sentido saida"><FiArrowLeft aria-hidden="true" /> Saída</span>;
}

/** A decisão de saída da passagem, quando a liberação decidiu algo. */
function DecisaoDaSaida({ d }) {
  if (!d) return <span className="saidas-badge muted" title="Nenhuma decisão de liberação para esta passagem">—</span>;
  const liberada = d.decision === 'abriu';
  return (
    <span className="saidas-ctrl">
      <span className={`saidas-decisao ${liberada ? 'ok' : 'bad'}`}>{liberada ? 'Liberada' : 'Bloqueada'}</span>
      <span className="saidas-motivo-sub">{d.reason_label}{d.mode === 'manual' && d.operator_name ? ` · por ${d.operator_name}` : ''}</span>
    </span>
  );
}

// Onda 200: a decisão da própria câmera LPR (o relé dela, pela lista que o controle de acesso carrega) e a conferência
// com o cadastro da plataforma — quem uma conhece e a outra não é carga faltando de um dos lados.
const CONFERENCIA = {
  confere: { classe: 'ok', texto: 'confere', dica: 'A câmera e a plataforma concordam' },
  camera_conhece: { classe: 'warn', texto: 'diverge', dica: 'A câmera abriu (a placa está na lista dela), mas a plataforma não conhece a placa: falta na carga da plataforma, ou a placa foi lida diferente' },
  plataforma_conhece: { classe: 'warn', texto: 'diverge', dica: 'A plataforma conhece a placa, mas a câmera não abriu: falta na carga da câmera, morador inativo ou visita vencida' },
  bloqueio: { classe: 'bad', texto: 'bloqueio', dica: 'A placa está na lista de bloqueio da câmera' },
};
function DecisaoDaCamera({ m, comConferencia = true }) {
  if (m.camera_opened === null || m.camera_opened === undefined) return <span className="saidas-badge muted" title="Sem a decisão da câmera (câmera do motor, ou passagem anterior a 23/10)">—</span>;
  const c = CONFERENCIA[m.camera_check];
  return (
    <span className="saidas-ctrl">
      <span className={`saidas-badge ${m.camera_opened ? 'ok' : 'muted'}`}>{m.camera_opened ? 'abriu' : 'não abriu'}{m.camera_list_label ? ` · ${m.camera_list_label}` : ''}</span>
      {comConferencia && c && c.texto !== 'confere' && <span className={`saidas-badge ${c.classe}`} title={c.dica}>{c.texto}</span>}
    </span>
  );
}

function ModalPassagem({ m, token, onFechar, podeLiberar, onLiberar, encerrada }) {
  const foto = fileUrl(m.image_path, token);
  const recorte = fileUrl(m.plate_crop_path, token);
  const d = m.exit_decision;
  return (
    <div className="saidas-modal-overlay" onClick={onFechar} role="presentation">
      <div className="saidas-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label={`${m.log_type === 'entrada' ? 'Entrada' : 'Saída'} de ${m.plate}`}>
        <div className="saidas-modal-cabecalho">
          <div>
            <PlacaVeicular placa={m.plate} tamanho="g" />
            <div className="saidas-modal-titulo"><Sentido tipo={m.log_type} /><span className="saidas-modal-motivo">{m.kind_label}</span></div>
            <div className="saidas-modal-sub">{m.camera || `câmera ${m.camera_id}`} · {dataHora(m.detected_at)}{m.raw_plate && m.raw_plate !== m.plate ? ` · lida como ${m.raw_plate}` : ''}</div>
          </div>
          <button type="button" className="saidas-btn-fechar" onClick={onFechar} aria-label="Fechar"><FiX /></button>
        </div>
        <div className="saidas-modal-corpo">
          <div className="saidas-modal-fotos">
            {foto ? <img className="saidas-foto" src={foto} alt={`Foto da leitura de ${m.plate}`} /> : <div className="saidas-foto saidas-foto--vazia">sem foto da leitura</div>}
            {recorte && <img className="saidas-recorte" src={recorte} alt={`Recorte da placa ${m.plate}`} />}
          </div>
          <dl className="saidas-modal-dados">
            <dt>Quem</dt><dd><Quem m={m} /></dd>
            {m.camera_opened !== null && m.camera_opened !== undefined && (
              <>
                <dt>Câmera LPR</dt><dd><DecisaoDaCamera m={m} comConferencia={false} /></dd>
                {CONFERENCIA[m.camera_check] && <><dt>Câmera × plataforma</dt><dd>{CONFERENCIA[m.camera_check].dica}</dd></>}
              </>
            )}
            {m.log_type === 'saida' && <><dt>Liberação da saída</dt><dd><DecisaoDaSaida d={d} /></dd></>}
            {d && d.mode === 'manual' && <><dt>Liberada por</dt><dd>{d.operator_name || d.operator_id || '—'}: {d.justification}</dd></>}
          </dl>
          {encerrada && <p className="saidas-detalhe-aviso">{encerrada}</p>}
          {podeLiberar && (
            <div className="saidas-modal-acoes">
              <button type="button" className="saidas-btn saidas-btn--liberar" onClick={() => onLiberar(m)}><FiUnlock /> Liberar agora</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * @param {Object} p
 * @param {boolean} p.ehCondominio   no condomínio a tela é a Movimentação inteira; no clube, a visão opcional
 * @param {string}  p.sentido        'todos' | 'entrada' | 'saida'
 * @param {Function} p.onSentido     troca o sentido (no clube, 'decisoes' volta às Saídas)
 * @param {Function} p.onLiberar     abre o modal de justificativa das Saídas com a decisão da passagem
 * @param {number}   p.recarregar    muda quando uma liberação pela tela termina
 */
export default function Movimentacao({ ehCondominio, sentido, onSentido, onLiberar, recarregar = 0, regrasDaVertical }) {
  const { token, socket, can } = useAuth();
  const podeLiberar = typeof can === 'function' ? !!can('exit_release_open') : false;

  const [filtros, setFiltros] = useState({ date_from: hojeLocal(), date_to: hojeLocal(), camera_id: '', kind: '', plate: '', check: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINA_PADRAO);
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [resumo, setResumo] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [detalhe, setDetalhe] = useState(null);
  const [telaGrande, setTelaGrande] = useState(() => { try { return localStorage.getItem('saidas.telaGrande') === '1'; } catch (_) { return false; } });

  const hoje = hojeLocal();
  const vendoHoje = filtros.date_from === hoje && filtros.date_to === hoje;
  const direction = sentido === 'entrada' || sentido === 'saida' ? sentido : '';

  const carregarLista = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const p = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      for (const [k, v] of Object.entries(filtros)) if (v) p.set(k, v);
      if (direction) p.set('direction', direction);
      const r = await api.get(`/api/exit-release/movements?${p.toString()}`);
      setItens(r.data.items || []); setTotal(r.data.total || 0);
    } catch (e) {
      setErro(e.response?.data?.error || 'Não foi possível carregar a movimentação');
    } finally { setCarregando(false); }
  }, [filtros, page, pageSize, direction]);

  const carregarResumo = useCallback(async () => {
    try { const r = await api.get(`/api/exit-release/movements/summary?date=${filtros.date_to}`); setResumo(r.data); } catch (_) { setResumo(null); }
  }, [filtros.date_to]);

  useEffect(() => { carregarLista(); }, [carregarLista, recarregar]);
  useEffect(() => { carregarResumo(); }, [carregarResumo, recarregar]);
  useEffect(() => { try { localStorage.setItem('saidas.telaGrande', telaGrande ? '1' : '0'); } catch (_) { /* só não lembra */ } }, [telaGrande]);

  // A passagem nova (ou a decisão nova) recarrega a primeira página de hoje — junta o que chega em rajada.
  const vivo = useRef({ vendoHoje, page, carregarLista, carregarResumo });
  vivo.current = { vendoHoje, page, carregarLista, carregarResumo };
  useEffect(() => {
    if (!socket || typeof socket.on !== 'function') return undefined;
    let t = null;
    const chegou = () => {
      if (t) return;
      t = setTimeout(() => {
        t = null;
        const v = vivo.current;
        if (v.vendoHoje && v.page === 1) v.carregarLista();
        v.carregarResumo();
      }, 1200);
    };
    socket.on('plate_detected', chegou);
    socket.on('exit_decision', chegou);
    return () => {
      if (t) clearTimeout(t);
      if (typeof socket.off === 'function') { socket.off('plate_detected', chegou); socket.off('exit_decision', chegou); }
    };
  }, [socket]);

  const camsEntrada = (resumo && resumo.cameras && resumo.cameras.entrada) || [];
  const camsSaida = (resumo && resumo.cameras && resumo.cameras.saida) || [];
  const temEntrada = !resumo || camsEntrada.length > 0;
  const temSaida = !resumo || camsSaida.length > 0;
  const camerasDoFiltro = direction === 'entrada' ? camsEntrada : direction === 'saida' ? camsSaida : [...camsEntrada, ...camsSaida];
  const aviso = resumo && !camsEntrada.length && camsSaida.length ? 'Esta instalação tem só câmera de saída: a movimentação mostra as saídas.'
    : resumo && camsEntrada.length && !camsSaida.length ? 'Esta instalação tem só câmera de entrada: a movimentação mostra as entradas.' : null;

  function mudarFiltro(k, v) { setFiltros(f => ({ ...f, [k]: v })); setPage(1); }
  function limparFiltros() { setFiltros({ date_from: hoje, date_to: hoje, camera_id: '', kind: '', plate: '', check: '' }); setPage(1); }
  const temCameraLpr = !!(resumo && resumo.tem_camera_lpr);   // Onda 200
  const conf = resumo && resumo.conferencia_camera;

  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setAgora(Date.now()), 30000); return () => clearInterval(t); }, []);
  const prazoDe = (d) => (d.open_until ? Date.parse(d.open_until) : Date.parse(d.created_at) + PRAZO_LIBERACAO_MS);
  const liberavel = (d) => !!d && (d.decision === 'recusou' || d.controller_status === 'sem_confirmacao') && !!d.device;
  const encerrada = (d) => liberavel(d) && (d.superseded || agora >= prazoDe(d));
  const podeLiberarLinha = (m) => podeLiberar && liberavel(m.exit_decision) && !encerrada(m.exit_decision);
  const motivoEncerrada = (d) => (d.superseded ? 'Esta placa já tem decisão mais nova nesta câmera' : `Bloqueio de ${hora(d.created_at)}: a liberação pela tela vale 5 min. Libere pela botoeira.`);
  // O modal de justificativa é o das Saídas: ele recebe a decisão, com a câmera da passagem.
  const liberar = (m) => onLiberar({ ...m.exit_decision, camera: m.camera });

  const lista = useMemo(() => (telaGrande ? itens.slice(0, TELA_GRANDE_LINHAS) : itens), [itens, telaGrande]);
  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));
  const mostraDecisao = temSaida && direction !== 'entrada';
  const quando = vendoHoje ? 'hoje' : `em ${dia(filtros.date_to)}`;
  const nomeCadastrados = ehCondominio ? 'moradores' : 'associados';

  const opcoesDeSentido = [
    ...(ehCondominio ? [] : [{ v: 'decisoes', t: 'Saídas (decisões)' }]),
    ...(temEntrada && temSaida ? [{ v: 'todos', t: 'Entradas e saídas' }] : []),
    ...(temEntrada ? [{ v: 'entrada', t: 'Entradas' }] : []),
    ...(temSaida ? [{ v: 'saida', t: 'Saídas' }] : []),
  ];

  return (
    <div className={`saidas-page${telaGrande ? ' saidas-page--grande' : ''}`}>
      <div className="saidas-cabecalho">
        <div>
          <h1 className="saidas-titulo">{ehCondominio ? <><FiRepeat /> Movimentação</> : <><FiLogOut /> Saídas</>}</h1>
          {!telaGrande && <p className="saidas-subtitulo">Cada {temEntrada && temSaida ? 'entrada e saída' : temSaida ? 'saída' : 'entrada'} registrada pelas câmeras, com a foto e o recorte da placa. As passagens novas chegam sozinhas.</p>}
        </div>
        <div className="saidas-acoes">
          <button type="button" className="saidas-btn" onClick={() => { carregarLista(); carregarResumo(); }} disabled={carregando} title="Recarregar"><FiRefreshCw /> {telaGrande ? '' : 'Atualizar'}</button>
          <button type="button" className="saidas-btn" onClick={() => setTelaGrande(v => !v)} title={telaGrande ? 'Voltar à tela normal' : 'Tela grande para a guarita'}>
            {telaGrande ? <FiMinimize2 /> : <FiMaximize2 />} {telaGrande ? 'Tela normal' : 'Tela grande'}
          </button>
        </div>
      </div>

      {aviso && <div className="saidas-situacao saidas-situacao--off"><span>{aviso}</span></div>}
      {resumo && resumo.liberacao_ligada && mostraDecisao && regrasDaVertical && (
        <div className={`saidas-situacao saidas-situacao--${resumo.abertura_automatica ? 'auto' : 'obs'}`}>
          <span>{resumo.abertura_automatica ? 'Abertura automática ligada: cada saída liberada pulsa o relé da controladora da câmera.' : 'Modo observação: a plataforma decide e registra, mas nenhum relé é acionado.'} <span className="saidas-regras">{regrasDaVertical}</span></span>
        </div>
      )}

      {resumo && (
        <div className="saidas-cartoes">
          {temEntrada && <div className="saidas-cartao"><span className="saidas-cartao-n">{resumo.entradas}</span><span className="saidas-cartao-l">entradas {quando}</span></div>}
          {temSaida && <div className="saidas-cartao"><span className="saidas-cartao-n">{resumo.saidas}</span><span className="saidas-cartao-l">saídas {quando}</span></div>}
          <div className="saidas-cartao ok"><span className="saidas-cartao-n">{resumo.cadastrados}</span><span className="saidas-cartao-l">passagens de {nomeCadastrados}</span></div>
          <div className="saidas-cartao"><span className="saidas-cartao-n">{resumo.visitantes}</span><span className="saidas-cartao-l">de visitantes</span></div>
          <div className={`saidas-cartao ${resumo.nao_cadastrados ? 'warn' : ''}`}><span className="saidas-cartao-n">{resumo.nao_cadastrados}</span><span className="saidas-cartao-l">de não cadastrados</span></div>
          {resumo.liberacao_ligada && temSaida && <div className="saidas-cartao bad"><span className="saidas-cartao-n">{resumo.bloqueadas}</span><span className="saidas-cartao-l">saídas bloqueadas</span></div>}
          {temCameraLpr && conf && (
            <button type="button" className={`saidas-cartao saidas-cartao--botao ${conf.divergencias ? 'warn' : 'ok'}`} onClick={() => mudarFiltro('check', 'diverge')}
              title={`A câmera abriu e a plataforma não conhece: ${conf.camera_conhece}. A plataforma conhece e a câmera não abriu: ${conf.plataforma_conhece}. Lista de bloqueio: ${conf.bloqueio}. Clique para ver só as divergências.`}>
              <span className="saidas-cartao-n">{conf.divergencias}</span><span className="saidas-cartao-l">divergências câmera × plataforma</span>
            </button>
          )}
        </div>
      )}

      {!telaGrande && (
        <div className="saidas-filtros">
          <label><span>De</span><input type="date" value={filtros.date_from} onChange={e => mudarFiltro('date_from', e.target.value)} /></label>
          <label><span>Até</span><input type="date" value={filtros.date_to} onChange={e => mudarFiltro('date_to', e.target.value)} /></label>
          {opcoesDeSentido.length > 1 && (
            <label><span>Mostrar</span>
              <select value={sentido} onChange={e => { onSentido(e.target.value); setPage(1); mudarFiltro('camera_id', ''); }} aria-label="Mostrar">
                {opcoesDeSentido.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
              </select>
            </label>
          )}
          <label><span>Câmera</span>
            <select value={filtros.camera_id} onChange={e => mudarFiltro('camera_id', e.target.value)}>
              <option value="">todas</option>
              {camerasDoFiltro.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label><span>Quem</span>
            <select value={filtros.kind} onChange={e => mudarFiltro('kind', e.target.value)}>
              <option value="">todos</option>
              <option value="cadastrado">{ehCondominio ? 'moradores' : 'associados'}</option>
              <option value="visitante">visitantes</option>
              <option value="nao_cadastrado">não cadastrados</option>
            </select>
          </label>
          {temCameraLpr && (
            <label><span>Conferência</span>
              <select value={filtros.check} onChange={e => mudarFiltro('check', e.target.value)} aria-label="Conferência">
                <option value="">todas</option>
                <option value="diverge">só divergências</option>
                <option value="abriu">a câmera abriu</option>
                <option value="nao_abriu">a câmera não abriu</option>
              </select>
            </label>
          )}
          <label><span>Placa</span><input type="text" value={filtros.plate} placeholder="ABC1D23" maxLength={10} onChange={e => mudarFiltro('plate', e.target.value.toUpperCase())} /></label>
          <button type="button" className="saidas-btn" onClick={limparFiltros} title="Voltar para hoje, sem filtros"><FiFilter /> Hoje</button>
        </div>
      )}

      {erro && <div className="saidas-erro">{erro}</div>}

      <div className="saidas-tabela-caixa">
        <table className="saidas-tabela">
          <thead>
            <tr>
              <th>Hora</th><th>Placa</th><th>Sentido</th><th>Câmera</th><th>Quem</th>
              {temCameraLpr && <th>Câmera LPR</th>}
              {mostraDecisao && <th>Liberação da saída</th>}
              {podeLiberar && mostraDecisao && <th aria-label="Ações" />}
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && (
              <tr><td colSpan={8} className="saidas-vazio">{carregando ? 'Carregando…' : 'Nenhuma passagem registrada no período.'}</td></tr>
            )}
            {lista.map(m => {
              const d = m.exit_decision;
              const classe = d ? (d.decision === 'abriu' ? 'ok' : 'bad') : (m.log_type === 'entrada' ? 'entrada' : 'saida');
              return (
                <tr key={m.id} className={`saidas-linha ${classe}`} onClick={() => setDetalhe(m)}>
                  <td className="saidas-hora" title={dataHora(m.detected_at)}>{vendoHoje || telaGrande ? hora(m.detected_at) : dataHora(m.detected_at)}</td>
                  <td><PlacaVeicular placa={m.plate} tamanho={telaGrande ? 'g' : 'p'} /></td>
                  <td><Sentido tipo={m.log_type} /></td>
                  <td>{m.camera || `câmera ${m.camera_id}`}</td>
                  <td><Quem m={m} /></td>
                  {temCameraLpr && <td><DecisaoDaCamera m={m} /></td>}
                  {mostraDecisao &&<td>{m.log_type === 'saida' ? <DecisaoDaSaida d={d} /> : null}</td>}
                  {podeLiberar && mostraDecisao && (
                    <td className="saidas-acao" onClick={e => e.stopPropagation()}>
                      {podeLiberarLinha(m) && (
                        <button type="button" className="saidas-btn saidas-btn--liberar saidas-btn--mini" onClick={() => liberar(m)} title="Pulsar o relé da controladora desta câmera, com justificativa"><FiUnlock /> Liberar</button>
                      )}
                      {d && encerrada(d) && <span className="saidas-badge muted" title={motivoEncerrada(d)}>liberação encerrada</span>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!telaGrande && (
        <Pagination currentPage={page} totalPages={totalPaginas} pageSize={pageSize} totalRecords={total}
          onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} itemLabel="passagem" />
      )}

      {detalhe && (
        <ModalPassagem m={detalhe} token={token} onFechar={() => setDetalhe(null)}
          podeLiberar={podeLiberarLinha(detalhe)}
          encerrada={detalhe.exit_decision && encerrada(detalhe.exit_decision) ? motivoEncerrada(detalhe.exit_decision) : null}
          onLiberar={(m) => { setDetalhe(null); liberar(m); }} />
      )}
    </div>
  );
}
